import { MouseEvent, useEffect, useState } from 'react';
import { MfaType } from '@bumble/app/__generated__/data-contracts';
import { CardRows, btnSx } from '@bumble/app/components/CardRows';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { useAccount } from '@bumble/app/hooks/use-account';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export const Account = () => {
  const api = useApi();
  const { account, isLoading, accountRefetch, session } = useAccount();
  const navigate = useNavigate();
  const [username, setUsername] = useState<string>(account?.username || '');
  const [password, setPassword] = useState<string>('');
  const [mfaEnabled, setMfaEnabled] = useState<boolean>(account?.mfa_enabled || false);
  const [metadata_url, setMetadataURL] = useState<string>(account?.username || '');
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [inactivityDays, setInactivityDays] = useState<string>('');

  const {
    data: accountIDP,
    isLoading: isIDPLoading
  } = useQuery(
    ['accountIDP'],
    api.getAccountIdpApiV1GetAccountIdpGet.bind(null),
    { enabled: session?.session_type === 'account'}
  );

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) =>
    void event.preventDefault();

  const updateAccount = useMutation(api.updateAccountApiV1UpdateAccountPost, {
    onSuccess() {
      toast.success('Account updated.');
      accountRefetch();
    },
    onError: apiError,
  });

  const updateAccountIdp = useMutation(api.updateAccountIdpApiV1UpdateAccountIdpPost, {
    onSuccess() {
      toast.success('Account IDP updated.');
    },
    onError: apiError,
  });

  const updateIamAccount = useMutation(api.updateIamUserApiV1UpdateIamUserPost, {
    onSuccess() {
      toast.success('Account updated.');
      accountRefetch();
    },
    onError: apiError,
  });

  const removeAccount = useMutation(api.removeAccountApiV1RemoveAccountPost, {
    onSuccess() {
      toast.success('Account removed.');
      navigate('/logout');
    },
    onError: apiError,
  });

  const enableEmailMfa = useMutation(api.accountEnableMfaApiV1AccountEnableMfaPost, {
    onSuccess() {
      toast.success('Multi-Factor Authentication enabled.');
    },
    onError: apiError,
  });

  const enableEmailMfaIam = useMutation(api.iamUserEnableMfaApiV1IamUserEnableMfaPost, {
    onSuccess() {
      toast.success('Multi-Factor Authentication enabled.');
      accountRefetch();
    },
    onError: apiError,
  });

  const disableEmailMfa = useMutation(api.accountDisableMfaApiV1AccountDisableMfaPost, {
    onSuccess() {
      toast.success('Multi-Factor Authentication disabled.');
    },
    onError: apiError,
  });

  const disableEmailMfaIam = useMutation(api.iamUserDisableMfaApiV1IamUserDisableMfaPost, {
    onSuccess() {
      toast.success('Multi-Factor Authentication disabled.');
      accountRefetch();
    },
    onError: apiError,
  });

  const accountInactivityLock = useMutation(api.accountInactivityLockApiV1AccountInactivityLockPost, {
    onSuccess(_data, variables) {
      toast.success(
        variables.enable
          ? 'Inactivity lock is on.' : 'Inactivity lock is off.',
      );
      accountRefetch();
    },
    onError: apiError,
  });

  const accountEnforceMfa = useMutation(api.accountEnforceMfaApiV1AccountEnforceMfaPost, {
    onSuccess(_data, variables) {
      toast.success(
        variables.enable
          ? 'MFA is now required for all IAM users. Existing and new IAM users must use MFA.'
          : 'Organization MFA enforcement is off.',
      );
      accountRefetch();
    },
    onError: apiError,
  });

  const handleUsernameSubmit = () => {
    if (account) {
      if (session?.session_type === 'account') {
        updateAccount.mutate({ username });
      } else {
        updateIamAccount.mutate({ name: username });
      }
    }
  };

  const handlePasswordSubmit = () => {
    if (account) {
      if (session?.session_type === 'account') {
        updateAccount.mutate({
          username: account?.username,
          password
        });
      } else {
        updateIamAccount.mutate({ password });
      }
    }
  };

  const handleMfaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    if (
      !isChecked &&
      session?.session_type === 'iam_user' &&
      account?.enforce_mfa
    ) {
      toast.warning(
        'MFA cannot be disabled while your organization requires MFA for all IAM users.',
      );
      return;
    }
    if (!isChecked && session?.session_type === 'account') {
      disableEmailMfa.mutate({});
    } else if (isChecked && session?.session_type === 'account') {
      enableEmailMfa.mutate({ type: MfaType.Email });
    }
    if (!isChecked && session?.session_type === 'iam_user') {
      disableEmailMfaIam.mutate({});
    } else if (isChecked && session?.session_type === 'iam_user') {
      enableEmailMfaIam.mutate({ type: MfaType.Email });
    }
    setMfaEnabled(isChecked);
  };

  const handleEnforceMfaGlobalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    accountEnforceMfa.mutate({ enable: e.target.checked });
  };

  const handleInactivityLockToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const n = parseInt(inactivityDays, 10);
    const inactivity_days =
      !Number.isNaN(n) && n >= 1 ? n : account?.inactivity_days ?? 90;
    accountInactivityLock.mutate({
      enable: e.target.checked,
      inactivity_days,
    });
  };

  const handleInactivityDaysUpdate = () => {
    const n = parseInt(inactivityDays, 10);
    if (Number.isNaN(n) || n < 1) {
      toast.error('Please enter a valid number of inactivity days (at least 1).');
      return;
    }
    accountInactivityLock.mutate({
      enable: !!account?.inactivity_lock,
      inactivity_days: n,
    });
  };

  const handleMetadataURLSubmit = () => {
    if (metadata_url) {
      updateAccountIdp.mutate({
        metalink: metadata_url,
      });
    }
  };

  const handleAccountDelete = () => {
    if (account) {
      removeAccount.mutate({
        username,
      });
    }
  };

  useEffect(() => {
    setUsername(account?.username || '');
    setMfaEnabled(account?.mfa_enabled || false);
    setInactivityDays(
      account?.inactivity_days != null ? String(account.inactivity_days) : '90',
    );
  }, [account]);

  const inactivityDaysForDesc =
    inactivityDays.trim() !== '' &&
    !Number.isNaN(parseInt(inactivityDays, 10)) &&
    parseInt(inactivityDays, 10) >= 1
      ? parseInt(inactivityDays, 10)
      : (account?.inactivity_days ?? 90);

  useEffect(() => {
    setMetadataURL(accountIDP?.data?.[0]?.metalink || '');
  }, [accountIDP]);

  if (isLoading || (session?.session_type === 'account' && isIDPLoading)) {
    return <Loading />;
  }
  return (
    <PageContent>
      <PageHeader pageName="Account Settings" />
      <Grid container spacing={5}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" elevation={0}>
            <CardRows
              label="Username"
              desc="The username cannot be updated at this time."
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="username"
                  label="Username"
                  variant="outlined"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              }
              action={
                <Button variant="contained" sx={btnSx} onClick={handleUsernameSubmit}>
                  Update
                </Button>
              }
            />
            <Divider />
            <CardRows
              label="Password"
              desc="Create a secure password to protect your account and ensure the confidentiality of your personal information."
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="password"
                  label="Password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              }
              action={
                <Button variant="contained" sx={btnSx} onClick={handlePasswordSubmit}>
                  Update
                </Button>
              }
            />
            <Divider />
            <CardRows
              label="Multi-Factor Authentication"
              desc="Enable Multi-Factor Authentication to add an extra layer of security to your account. When enabled, you will be required to enter a verification code sent to your email address upon login."
              value={null}
              action={
                <FormControlLabel
                  value="top"
                  control={
                    <Switch
                      color="primary"
                      checked={mfaEnabled}
                      onChange={handleMfaChange}
                      disabled={
                        session?.session_type === 'iam_user' &&
                        !!account?.enforce_mfa &&
                        mfaEnabled
                      }
                    />
                  }
                  label={mfaEnabled ? 'Enabled' : 'Disabled'}
                  labelPlacement="bottom"
                  sx={(theme) => ({ '&:hover': { color: theme.palette.primary.main } })}
                  componentsProps={{
                    typography: {
                      sx: (theme) => ({
                        fontSize: 12,
                        color: !mfaEnabled ? theme.palette.grey[600] : theme.palette.primary.main,
                      }),
                    },
                  }}
                />
              }
            />
            <Divider />
            {session?.session_type === 'account' && (
              <>
                <CardRows
                  label="SAML integration"
                  desc="Enable Single Sign on via SAML integration with your corporate IDP."
                  value={
                    <TextField
                      fullWidth
                      size="small"
                      id="metadata_url"
                      label="Bumblebee app metadata URL"
                      variant="outlined"
                      value={metadata_url}
                      onChange={(e) => setMetadataURL(e.target.value)}
                    />
                  }
                  action={
                    <Button variant="contained" sx={btnSx} onClick={handleMetadataURLSubmit}>
                      Update
                    </Button>
                  }
                />
                <Divider />
                <CardRows
                  color="error"
                  label="Delete Account"
                  desc="By clicking the 'Delete' button, your account will be permanently removed along with any associated connections. This action is irreversible and will result in the complete deletion of your account, including all data and connections. Please proceed with caution as this action cannot be undone."
                  value={null}
                  action={
                    <Button
                      variant="outlined"
                      color="error"
                      sx={btnSx}
                      onClick={() => setShowConfirmDelete(true)}
                    >
                      Delete
                    </Button>
                  }
                />
              </>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" elevation={0}>
            <CardContent>
              <Typography variant="subtitle1">What are Settings?</Typography>
              <Typography variant="body2">
                The Settings page empowers you to manage crucial aspects of your account.
                Here, you can update your username, create a strong password to enhance security,
                and even have the option to delete your account if needed.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleAccountDelete}
          title="Delete your account?"
          description="Are you certain you wish to proceed with resetting this endpoint? Please note that this action is irreversible and will result in the permanent reset of the endpoint. Proceeding with this action will reset all associated settings and configurations to their default state."
          confirmationPhrase="confirm"
        />
      )}
      {session?.session_type === 'account' && (
        <>
          <Typography sx={{ fontSize: 24, fontWeight: 'bold', my: 4 }}>Global Settings</Typography>
          <Grid container spacing={5}>
            <Grid item xs={12} md={8}>
              <Card variant="outlined" elevation={0}>
                <CardRows
                  label="Enforce MFA"
                  desc="Enforce MFA for all IAM users under the account including those belonging to entities under the account."
                  value={null}
                  action={
                    <FormControlLabel
                      value="top"
                      control={
                        <Switch
                          color="primary"
                          checked={!!account?.enforce_mfa}
                          onChange={handleEnforceMfaGlobalChange}
                          disabled={accountEnforceMfa.isLoading}
                        />
                      }
                      label={account?.enforce_mfa ? 'Enabled' : 'Disabled'}
                      labelPlacement="bottom"
                      sx={(theme) => ({ '&:hover': { color: theme.palette.primary.main } })}
                      componentsProps={{
                        typography: {
                          sx: (theme) => ({
                            fontSize: 12,
                            color: !account?.enforce_mfa
                              ? theme.palette.grey[600]
                              : theme.palette.primary.main,
                          }),
                        },
                      }}
                    />
                  }
                />
                <Divider />
                <CardRows
                  label="Inactivity Lock"
                  desc={`Lock out an IAM user if they haven't logged in for ${inactivityDaysForDesc} days. Only account holder can unlock the IAM user.`}
                  value={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'center' },
                        justifyContent: { md: 'flex-end' },
                        gap: { xs: 2, md: 3 },
                        width: '100%',
                      }}
                    >
                      <FormControlLabel
                        value="top"
                        control={
                          <Switch
                            color="primary"
                            checked={!!account?.inactivity_lock}
                            onChange={handleInactivityLockToggle}
                            disabled={accountInactivityLock.isLoading}
                          />
                        }
                        label={account?.inactivity_lock ? 'Enabled' : 'Disabled'}
                        labelPlacement="bottom"
                        sx={(theme) => ({
                          '&:hover': { color: theme.palette.primary.main },
                          mx: 4,
                          alignSelf: { xs: 'center', md: 'center' },
                        })}
                        componentsProps={{
                          typography: {
                            sx: (theme) => ({
                              fontSize: 12,
                              color: account?.inactivity_lock
                                ? theme.palette.primary.main
                                : theme.palette.grey[600],
                            }),
                          },
                        }}
                      />
                      {account?.inactivity_lock && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            gap: 2,
                            flexShrink: 0,
                          }}
                        >
                          <TextField
                            size="small"
                            id="inactivityDays"
                            label="Inactivity Days"
                            variant="outlined"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={inactivityDays}
                            onChange={(e) => setInactivityDays(e.target.value)}
                            sx={{ minWidth: { sm: 160 } }}
                          />
                          <Button
                            variant="contained"
                            sx={btnSx}
                            type="button"
                            onClick={handleInactivityDaysUpdate}
                            disabled={accountInactivityLock.isLoading}
                          >
                            Update
                          </Button>
                        </Box>
                      )}
                    </Box>
                  }
                />
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </PageContent>
  );
};
